<template>
    <div></div>
</template>

<script>
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { Style, Circle as CircleStyle, Fill, Stroke, Text } from 'ol/style';
import Overlay from 'ol/Overlay';
import { fromLonLat } from 'ol/proj';

const MS_PER_YEAR = 1000 * 60 * 60 * 24 * 365.25;

export default {
    name: 'Population',
    data() {
        return {
            map: this.$store.getters['map/getMap'],
            baseYear: 2025,
            overlay: null,
            populationLayer: null,
            populationFeatures: [],
            selectedFeature: null,
            intervalId: null,
            countryCoords: {
                iran:        { center: [53.688, 32.4279], basePopulation: 93000000,   growthRate: 0.006 },
                france:      { center: [2.2137, 46.2276], basePopulation: 67000000,   growthRate: 0.002 },
                brazil:      { center: [-51.9253, -14.235], basePopulation: 213000000, growthRate: 0.004 },
                usa:         { center: [-98.5795, 39.8283], basePopulation: 347275807, growthRate: 0.005 },
                canada:      { center: [-106.3468, 56.1304], basePopulation: 38000000,  growthRate: 0.002 },
                iraq:        { center: [43.6793, 33.2232], basePopulation: 45600000,  growthRate: 0.025 },
                russia:      { center: [105.3188, 61.5240], basePopulation: 144000000, growthRate: -0.003 },
                china:       { center: [104.1954, 35.8617], basePopulation: 1416000000, growthRate: -0.001 },
                india:       { center: [78.9629, 20.5937], basePopulation: 1463865525, growthRate: 0.009 },
                australia:   { center: [133.7751, -25.2744], basePopulation: 27000000,  growthRate: 0.013 },
                korea:       { center: [127.7669, 35.9078], basePopulation: 52000000,  growthRate: -0.0015 },
                japan:       { center: [138.2529, 36.2048], basePopulation: 122000000, growthRate: -0.005 },
                mexico:      { center: [-102.5528, 23.6345], basePopulation: 133000000, growthRate: 0.010 },
                argentina:   { center: [-63.6167, -38.4161], basePopulation: 46400000,  growthRate: 0.008 },
                england:     { center: [-1.1743, 52.3555], basePopulation: 57700000,  growthRate: 0.004 },
                italy:       { center: [12.5674, 41.8719], basePopulation: 58500000,  growthRate: -0.003 },
                spain:       { center: [-3.7492, 40.4637], basePopulation: 48000000,  growthRate: 0.002 },
                germany:     { center: [10.4515, 51.1657], basePopulation: 84000000,  growthRate: 0.001 },
                pakistan:    { center: [69.3451, 30.3753], basePopulation: 240000000, growthRate: 0.020 },
                egypt:       { center: [30.8025, 26.8206], basePopulation: 111000000, growthRate: 0.018 },
                nigeria:     { center: [8.6753, 9.0820], basePopulation: 223000000, growthRate: 0.025 },
                south_africa:{ center: [22.9375, -30.5595], basePopulation: 60300000,  growthRate: 0.012 },
                saudi_arabia:{ center: [45.0792, 23.8859], basePopulation: 35800000,  growthRate: 0.018 },
                turkey:      { center: [35.2433, 38.9637], basePopulation: 87000000,  growthRate: 0.010 },
                thailand:    { center: [100.9925, 15.8700], basePopulation: 71000000,  growthRate: 0.007 },
                indonesia:   { center: [113.9213, -0.7893], basePopulation: 277000000, growthRate: 0.011 },
                philippines: { center: [121.7740, 12.8797], basePopulation: 118000000, growthRate: 0.015 }
            }
        };
    },
    created() {
        this.baseTimestamp = new Date(`${this.baseYear}-01-01T00:00:00Z`).getTime();
        Object.values(this.countryCoords).forEach((c) => {
            c.k = Math.log(1 + c.growthRate);
        });
        this.populationEl = null;
        this.clickHandler = null;
        this.resolutionHandler = null;
    },
    mounted() {
        const loader = this.$loading.show({
            loader: "dots",
        });
        setTimeout(function () {
            loader.hide();
        }, 700);
        this.addPopulationMarkers();
        this.setupMapInteractions();
        this.startPopulationUpdates();
    },
    beforeDestroy() {
        clearInterval(this.intervalId);
        this.closePopup();
        if (this.clickHandler) this.map.un('singleclick', this.clickHandler);
        if (this.resolutionHandler) this.map.getView().un('change:resolution', this.resolutionHandler);
        if (this.populationLayer) this.map.removeLayer(this.populationLayer);
    },
    methods: {
        addPopulationMarkers() {
            const features = [];
            Object.entries(this.countryCoords).forEach(([name, data]) => {
                const population = this.getPopulationAt(data);
                const feature = new Feature({
                    geometry: new Point(fromLonLat(data.center)),
                    name,
                    population,
                    where: fromLonLat(data.center),
                });
                feature.setStyle(this.getStyle(population));
                features.push(feature);
            });
            this.populationFeatures = features;
            const source = new VectorSource({ features });
            const layer = new VectorLayer({ source });
            this.populationLayer = layer;
            this.map.addLayer(layer);
        },
        setupMapInteractions() {
            this.clickHandler = (e) => {
                const feature = this.map.forEachFeatureAtPixel(e.pixel, (f) => f);
                if (!feature) {
                    this.closePopup();
                    if (this.selectedFeature) {
                        const pop = this.selectedFeature.get('population');
                        this.selectedFeature.setStyle(this.getStyle(pop));
                        this.selectedFeature = null;
                    }
                    return;
                }
                if (this.selectedFeature && this.selectedFeature !== feature) {
                    const prevPop = this.selectedFeature.get('population');
                    this.selectedFeature.setStyle(this.getStyle(prevPop));
                }
                this.selectedFeature = feature;
                const population = feature.get('population');
                feature.setStyle(this.getSelectedStyle(population));
                this.map.getView().animate({
                    center: feature.get('where') || e.coordinate,
                    duration: 250,
                });
                setTimeout(() => this.showOverlay(feature), 300);
            };
            this.resolutionHandler = () => this.closePopup();
            this.map.on('singleclick', this.clickHandler);
            this.map.getView().on('change:resolution', this.resolutionHandler);
        },
        getStyle(population) {
            return new Style({
                image: new CircleStyle({
                    radius: 10,
                    fill: new Fill({ color: 'rgba(0, 51, 102, 0.75)' }),
                    stroke: new Stroke({ color: '#001F3F', width: 2 }),
                }),
                text: new Text({
                    text: this.formatPopulation(population),
                    offsetY: -22,
                    font: '600 13px "Segoe UI", Roboto, sans-serif',
                    fill: new Fill({ color: '#f0f0f0' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                }),
            });
        },
        getSelectedStyle(population) {
            return new Style({
                image: new CircleStyle({
                    radius: 14,
                    fill: new Fill({ color: 'rgba(0, 32, 96, 0.9)' }),
                    stroke: new Stroke({ color: '#000814', width: 3 }),
                }),
                text: new Text({
                    text: this.formatPopulation(population),
                    offsetY: -26,
                    font: '700 14px "Segoe UI", Roboto, sans-serif',
                    fill: new Fill({ color: '#ffffff' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                }),
            });
        },
        showOverlay(feature) {
            this.closePopup();
            const container = document.createElement('div');
            container.className = 'ol-popup';
            container.style.background = '#fff';
            container.style.padding = '10px';
            container.style.borderRadius = '6px';
            container.style.boxShadow = '0 1px 4px rgba(0,0,0,0.2)';
            container.style.width = '250px';
            container.style.position = 'absolute';
            container.style.top = '15px';
            container.style.left = '50%';
            container.style.transform = 'translateX(-50%)';
            const title = document.createElement('h4');
            title.innerText = feature.get('name').toUpperCase();
            title.style.textAlign = 'center';
            title.style.fontWeight = 'bold';
            title.style.margin = '0 0 6px 0';
            const population = document.createElement('p');
            population.innerText = `Population: ${this.formatPopulation(feature.get('population'))}`;
            population.style.margin = '0';
            population.style.textAlign = 'center';
            this.populationEl = population;
            container.appendChild(title);
            container.appendChild(population);
            this.overlay = new Overlay({
                element: container,
                positioning: 'bottom-center',
                autoPan: true,
                autoPanMargin: 20,
                autoPanAnimation: { duration: 100 },
            });
            this.map.addOverlay(this.overlay);
            this.overlay.setPosition(feature.get('where'));
        },
        closePopup() {
            if (this.overlay) {
                this.map.removeOverlay(this.overlay);
                this.overlay = null;
            }
            this.populationEl = null;
        },
        formatPopulation(pop) {
            return pop.toLocaleString('en-US');
        },
        startPopulationUpdates() {
            this.intervalId = setInterval(() => {
                this.populationFeatures.forEach((feature) => {
                    const data = this.countryCoords[feature.get('name')];
                    const currentPop = this.getPopulationAt(data);
                    feature.set('population', currentPop);
                    const style = feature.getStyle();
                    if (style && style.getText()) {
                        style.getText().setText(this.formatPopulation(currentPop));
                        feature.changed();
                    }
                    if (this.selectedFeature === feature && this.populationEl) {
                        this.populationEl.innerText = `Population: ${this.formatPopulation(currentPop)}`;
                    }
                });
            }, 1000);
        },
        getPopulationAt(country) {
            const yearsPassed = (Date.now() - this.baseTimestamp) / MS_PER_YEAR;
            return Math.round(country.basePopulation * Math.exp(country.k * yearsPassed));
        }
    },
};
</script>
